import type { Ref, ComponentPublicInstance } from 'vue'

interface StickyNavElement extends ComponentPublicInstance {
  $el: HTMLElement
}

interface StickyNavState {
  readonly stickyNavStartingPosition: Ref<number>
  readonly showStickySpacer: Ref<boolean>
  readonly pageHeaderHeight: Ref<number>
  readonly isStuck: Ref<boolean>
}

interface StickyNavOptions {
  readonly stickyNav: Ref<StickyNavElement | undefined>
}

interface StickyNavReturn extends StickyNavState {
  readonly setupStickyNav: () => void
  readonly cleanupStickyNav: () => void
  readonly updateInitialPositions: () => void
}

export function useStickyNav(options: StickyNavOptions): StickyNavReturn {
  const state: StickyNavState = {
    stickyNavStartingPosition: ref(0),
    showStickySpacer: ref(false),
    pageHeaderHeight: ref(0),
    isStuck: ref(false),
  }

  let ticking = false
  let navObserver: IntersectionObserver | null = null
  let resizeTimeout: ReturnType<typeof setTimeout>

  const observerOptions = {
    threshold: [0, 1],
    rootMargin: '-1px 0px 0px 0px', // 1px offset to prevent edge cases
  }

  function isStickyNavVisible(): boolean {
    if (!options.stickyNav.value?.$el) {
      return false
    }

    const { display } = window.getComputedStyle(options.stickyNav.value.$el)
    return display === 'block'
  }

  function updateStickyState(): void {
    if (!isStickyNavVisible() || !options.stickyNav.value?.$el) {
      return
    }

    const navElement = options.stickyNav.value.$el
    const threshold =
      state.stickyNavStartingPosition.value - state.pageHeaderHeight.value

    // Calculate if nav should be stuck based on scroll position
    const shouldBeStuck = window.scrollY > threshold

    // Only update if we have a real state change
    if (shouldBeStuck !== state.isStuck.value) {
      state.isStuck.value = shouldBeStuck
      state.showStickySpacer.value = shouldBeStuck
      navElement.classList.toggle('is-stuck', shouldBeStuck)
    }
  }

  function handleScroll(): void {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        updateStickyState()
        ticking = false
      })
      ticking = true
    }
  }

  function updateInitialPositions(): void {
    if (!options.stickyNav.value?.$el) return

    const header = document.querySelector('.page-header')
    state.pageHeaderHeight.value =
      header instanceof HTMLElement ? header.offsetHeight : 0

    // Get the nav's initial position relative to the document
    const navRect = options.stickyNav.value.$el.getBoundingClientRect()
    state.stickyNavStartingPosition.value = navRect.top + window.scrollY

    // Force an immediate check of sticky state
    updateStickyState()
  }

  function debouncedResizeListener(): void {
    clearTimeout(resizeTimeout)
    resizeTimeout = setTimeout(() => {
      updateInitialPositions()
    }, 150)
  }

  function setupStickyNav(): void {
    window.addEventListener('resize', debouncedResizeListener)
    window.addEventListener('scroll', handleScroll, { passive: true })

    // Set up intersection observer for nav element
    if (options.stickyNav.value?.$el) {
      navObserver = new IntersectionObserver((entries) => {
        const entry = entries[0]
        if (entry) {
          updateStickyState()
        }
      }, observerOptions)

      navObserver.observe(options.stickyNav.value.$el)
    }
  }

  function cleanupStickyNav(): void {
    window.removeEventListener('resize', debouncedResizeListener)
    window.removeEventListener('scroll', handleScroll)
    if (navObserver) {
      navObserver.disconnect()
    }
    clearTimeout(resizeTimeout)
  }

  return {
    ...toRefs(state),
    setupStickyNav,
    cleanupStickyNav,
    updateInitialPositions,
  }
}
